import { useState, useRef, useEffect } from "react";

/**
 * Camera/microphone test-and-select popup shown from Profile Settings ->
 * "Video conferencing" -> Check. Independent from the call-time device
 * selection in useVideoCall/useMediaDevices — this one persists its choice
 * under plain 'selectedCamera'/'selectedMicrophone' localStorage keys and
 * includes a live mic level meter via Web Audio's AnalyserNode.
 */
export function useDeviceCheck({ showSavedToast }) {
  // eslint-disable-next-line no-unused-vars
  const [isCheckingDevices, setIsCheckingDevices] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [deviceCheckResult, setDeviceCheckResult] = useState(null);
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMicrophone, setSelectedMicrophone] = useState('');
  const [availableCameras, setAvailableCameras] = useState([]);
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneStreamRef = useRef(null);
  const [microphoneLevel, setMicrophoneLevel] = useState(0);
  const [cameraError, setCameraError] = useState(null);

  const openDeviceSettings = () => {
    setShowDeviceSettings(true);
  };

  const closeDeviceSettings = () => {
    // Останавливаем поток камеры при закрытии
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }

    // Останавливаем поток микрофона при закрытии
    if (microphoneStreamRef.current) {
      microphoneStreamRef.current.getTracks().forEach(track => track.stop());
      microphoneStreamRef.current = null;
    }

    // Закрываем AudioContext
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setMicrophoneLevel(0);
    setShowDeviceSettings(false);
  };

  const saveDeviceSettings = () => {
    localStorage.setItem('selectedCamera', selectedCamera);
    localStorage.setItem('selectedMicrophone', selectedMicrophone);
    closeDeviceSettings();
    showSavedToast();
  };

  // Функция для запуска камеры
  const startCamera = async (deviceId) => {
    try {
      console.log('Starting camera with deviceId:', deviceId);
      setCameraError(null);

      // Останавливаем предыдущий поток
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: deviceId ? { deviceId: { exact: deviceId } } : true,
        audio: false
      };

      console.log('Camera constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('Camera stream obtained:', stream);

      setCameraStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        console.log('Video element updated with stream');
      } else {
        console.error('Video ref is null');
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      setCameraError(error.message);
    }
  };

  // Функция для запуска микрофона и мониторинга уровня звука
  const startMicrophone = async (deviceId) => {
    try {
      // Останавливаем предыдущий поток микрофона
      if (microphoneStreamRef.current) {
        microphoneStreamRef.current.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        video: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      microphoneStreamRef.current = stream;

      // Создаем AudioContext для анализа звука
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Запускаем мониторинг уровня звука
      monitorMicrophoneLevel();
    } catch (error) {
      console.error('Error starting microphone:', error);
    }
  };

  // Функция для мониторинга уровня микрофона
  const monitorMicrophoneLevel = () => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateLevel = () => {
      analyser.getByteFrequencyData(dataArray);

      // Вычисляем средний уровень звука
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const average = sum / dataArray.length;
      const level = Math.round((average / 255) * 100);

      setMicrophoneLevel(level);

      if (microphoneStreamRef.current && microphoneStreamRef.current.active) {
        requestAnimationFrame(updateLevel);
      }
    };

    updateLevel();
  };

  // Функция для получения доступных устройств
  const getDevices = async () => {
    setIsLoadingDevices(true);
    try {
      // Сначала запрашиваем разрешения на доступ к камере и микрофону
      await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });

      // После получения разрешений получаем список устройств
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      const microphones = devices.filter(device => device.kind === 'audioinput');

      setAvailableCameras(cameras);
      setAvailableMicrophones(microphones);

      // Устанавливаем первое доступное устройство по умолчанию
      if (cameras.length > 0) {
        if (!selectedCamera) {
          setSelectedCamera(cameras[0].deviceId);
        }
        // Запускаем камеру для предварительного просмотра
        setTimeout(() => startCamera(selectedCamera || cameras[0].deviceId), 100);
      }
      if (microphones.length > 0) {
        if (!selectedMicrophone) {
          setSelectedMicrophone(microphones[0].deviceId);
        }
        // Запускаем микрофон для мониторинга уровня звука
        setTimeout(() => startMicrophone(selectedMicrophone || microphones[0].deviceId), 200);
      }
    } catch (error) {
      console.error('Error getting devices:', error);
      // Если пользователь отклонил разрешения, показываем сообщение
      alert('Для настройки устройств необходимо разрешить доступ к камере и микрофону');
    } finally {
      setIsLoadingDevices(false);
    }
  };

  // Загружаем устройства при открытии попапа
  useEffect(() => {
    if (showDeviceSettings) {
      getDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeviceSettings]);

  // Загружаем сохраненные настройки при монтировании компонента
  useEffect(() => {
    const savedCamera = localStorage.getItem('selectedCamera');
    const savedMicrophone = localStorage.getItem('selectedMicrophone');

    if (savedCamera) setSelectedCamera(savedCamera);
    if (savedMicrophone) setSelectedMicrophone(savedMicrophone);
  }, []);

  return {
    showDeviceSettings,
    openDeviceSettings, closeDeviceSettings, saveDeviceSettings,
    selectedCamera, setSelectedCamera,
    selectedMicrophone, setSelectedMicrophone,
    availableCameras, availableMicrophones,
    isLoadingDevices, videoRef, cameraError, microphoneLevel,
    startCamera, startMicrophone,
  };
}
